import { useMutation, UseMutationOptions } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import {
  SchemaPresetsCreateResponse,
  SchemaResponse,
  SchemaCreateRequest,
  SchemaUpdateRequest,
} from 'src/types/TSchemes';
import {
  createSchemasFromPresets,
  createSchema,
  updateSchema,
  deleteSchema,
} from '../functions/schemaApi';

/**
 * Mutation hook for creating schemas from presets
 * Note: Does not auto-invalidate queries to allow for delayed refetch
 */
export function useCreateSchemaFromPresetsMutation(
  mutationOptions?: UseMutationOptions<SchemaPresetsCreateResponse, FetchError, string[]>,
) {
  const accessToken = useAccessToken();

  const mutationFn = async (presetIds: string[]) => {
    const result = await createSchemasFromPresets(accessToken, presetIds);
    if ('error' in result) {
      throw result;
    }
    return result as SchemaPresetsCreateResponse;
  };

  return useMutation(mutationFn, mutationOptions);
}

/**
 * Mutation hook for creating a new custom schema
 * Note: Does not auto-invalidate queries to allow for delayed refetch
 */
export function useCreateSchemaMutation(
  mutationOptions?: UseMutationOptions<SchemaResponse, FetchError, SchemaCreateRequest>,
) {
  const accessToken = useAccessToken();

  const mutationFn = async (data: SchemaCreateRequest) => {
    const result = await createSchema(accessToken, data);
    if ('error' in result) {
      throw result;
    }
    return result as SchemaResponse;
  };

  return useMutation(mutationFn, mutationOptions);
}

/**
 * Mutation hook for updating an existing schema
 * Note: Does not auto-invalidate queries to allow for delayed refetch
 */
export function useUpdateSchemaMutation(
  schemaId: number,
  mutationOptions?: UseMutationOptions<SchemaResponse, FetchError, SchemaUpdateRequest>,
) {
  const accessToken = useAccessToken();

  const mutationFn = async (data: SchemaUpdateRequest) => {
    const result = await updateSchema(accessToken, schemaId, data);
    if ('error' in result) {
      throw result;
    }
    return result as SchemaResponse;
  };

  return useMutation(mutationFn, mutationOptions);
}

/**
 * Mutation hook for deleting a schema
 * Note: Does not auto-invalidate queries to allow for delayed refetch
 */
export function useDeleteSchemaMutation(
  mutationOptions?: UseMutationOptions<any, FetchError, number>,
) {
  const accessToken = useAccessToken();

  const mutationFn = async (schemaId: number) => {
    const result = await deleteSchema(accessToken, schemaId);
    if ('error' in result) {
      throw result;
    }
    return result;
  };

  return useMutation(mutationFn, mutationOptions);
}
